export function timezoneLocation(timezone: string) {
  return timezone.split("/").at(-1)?.replaceAll("_", " ") ?? timezone
}

export function validScheduleTimezone(timezone: string | null | undefined) {
  if (!timezone) return null

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format()
    return timezone
  } catch {
    return null
  }
}

function localDateKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

function dateKeyInTimezone(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((value) => value.type === type)?.value

  return `${part("year")}-${part("month")}-${part("day")}`
}

function hourInTimezone(date: Date, timezone: string) {
  const hour = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .find((part) => part.type === "hour")?.value

  return Number(hour)
}

export function scheduleCandidate(date: Date, hour: number, minute: number) {
  const candidate = new Date(date)
  candidate.setHours(hour, minute, 0, 0)
  return candidate
}

export function isWithinBusinessHours(date: Date, timezone: string, startHour: number, endHour: number) {
  const hour = hourInTimezone(date, timezone)
  return hour >= startHour && hour <= endHour
}

export function availableScheduleTimes(
  date: Date,
  now: Date,
  recipientTimezone: string | null,
  startHour: number,
  endHour: number
) {
  const firstAdvisorHour = recipientTimezone ? 0 : startHour
  const lastAdvisorHour = recipientTimezone ? 23 : endHour
  const values: string[] = []

  for (let hour = firstAdvisorHour; hour <= lastAdvisorHour; hour++) {
    const candidate = scheduleCandidate(date, hour, 0)
    if (candidate <= now) continue

    const withinBusinessHours = recipientTimezone
      ? isWithinBusinessHours(candidate, recipientTimezone, startHour, endHour)
      : hour >= startHour && hour <= endHour
    if (!withinBusinessHours) continue

    values.push(`${hour.toString().padStart(2, "0")}:00`)
  }

  return values
}

export function firstAvailableScheduleSelection(
  now: Date,
  recipientTimezone: string | null,
  startHour: number,
  endHour: number,
  maxDays: number
) {
  for (let offset = 0; offset <= maxDays; offset++) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)
    const time = availableScheduleTimes(date, now, recipientTimezone, startHour, endHour)[0]
    if (time) return { date, time }
  }

  return null
}

export function recipientTimeLabel(candidate: Date, timezone: string) {
  const datePrefix =
    localDateKey(candidate) === dateKeyInTimezone(candidate, timezone)
      ? ""
      : `${new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(candidate)} `
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(candidate)

  return `${datePrefix}${time} ${timezoneLocation(timezone)}`
}

export function recipientSummaryLabel(candidate: Date, timezone: string) {
  const dateTime = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(candidate)

  return `${dateTime} ${timezoneLocation(timezone)}`
}
